/**
 * Admin Hashtag Gallery Tab
 * Manage community photo submissions with bulk actions
 */
import { useState, useEffect } from "react";
import { 
  Image, Check, X, Star, Trash2, Filter, Loader2,
  Heart, User, Instagram, Mail, Cat, CheckSquare, Square
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700"
};

const AdminHashtagGalleryTab = () => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetchPhotos();
    fetchStats();
  }, [filter]);

  const fetchPhotos = async () => {
    try {
      const url = filter === "all"
        ? `${API_URL}/api/hashtag-gallery/all`
        : `${API_URL}/api/hashtag-gallery/all?status=${filter}`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      const data = await res.json();
      setPhotos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch photos:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hashtag-gallery/stats/overview`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handleApprove = async (photoId) => {
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/${photoId}/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: "approved" })
      });
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to approve photo:", err);
    }
  };

  const handleReject = async (photoId) => {
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/${photoId}/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: "rejected" })
      });
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to reject photo:", err);
    }
  };

  // Bulk selection functions
  const toggleSelect = (photoId) => {
    setSelectedIds(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === photos.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(photos.map(p => p.photo_id));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Approve ${selectedIds.length} photo(s)?`)) return;
    
    setBulkLoading(true);
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/bulk/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ photo_ids: selectedIds, status: "approved" })
      });
      setSelectedIds([]);
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to bulk approve:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Reject ${selectedIds.length} photo(s)?`)) return;
    
    setBulkLoading(true);
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/bulk/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ photo_ids: selectedIds, status: "rejected" })
      });
      setSelectedIds([]);
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to bulk reject:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleFeatured = async (photoId, currentFeatured) => {
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/${photoId}/feature`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ featured: !currentFeatured })
      });
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to toggle featured:", err);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Delete this photo permanently?")) return;
    
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/${photoId}`, {
        method: "DELETE",
        credentials: 'include'
      });
      setShowModal(false);
      fetchPhotos();
      fetchStats();
    } catch (err) {
      console.error("Failed to delete photo:", err);
    }
  };

  const openPhotoDetail = (photo) => {
    setSelectedPhoto(photo);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-hashtag-gallery-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-stone-900">{stats.total || 0}</p>
            <p className="text-sm text-stone-500">Total</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-700">{stats.pending || 0}</p>
            <p className="text-sm text-yellow-600">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
            <p className="text-sm text-stone-500">Approved</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected || 0}</p>
            <p className="text-sm text-stone-500">Rejected</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.featured || 0}</p>
            <p className="text-sm text-stone-500">Featured</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.adopted_cats || 0}</p>
            <p className="text-sm text-stone-500">CatPals+ Cats</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Bulk Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-stone-500" />
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setSelectedIds([]); }}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All Photos</option>
          </select>
          {filter === "pending" && stats.pending > 0 && (
            <Badge className="bg-yellow-500 text-white">{stats.pending} awaiting review</Badge>
          )}
        </div>
        
        {/* Bulk Actions */}
        {photos.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              className="gap-2"
            >
              {selectedIds.length === photos.length ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {selectedIds.length === photos.length ? "Deselect All" : "Select All"}
            </Button>
            
            {selectedIds.length > 0 && (
              <>
                <Badge variant="secondary">{selectedIds.length} selected</Badge>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 gap-1"
                  onClick={handleBulkApprove}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Approve All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 gap-1"
                  onClick={handleBulkReject}
                  disabled={bulkLoading}
                >
                  <X className="w-4 h-4" />
                  Reject All
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Photos Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
        </div>
      ) : photos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Image className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-semibold text-stone-700 mb-2">No photos found</h3>
            <p className="text-stone-500">
              {filter === "pending" 
                ? "No photos awaiting review"
                : "No photos match this filter"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map(photo => (
            <Card 
              key={photo.photo_id} 
              className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group ${
                selectedIds.includes(photo.photo_id) ? "ring-2 ring-amber-500" : ""
              }`}
            >
              <div className="aspect-square relative" onClick={() => openPhotoDetail(photo)}>
                <img
                  src={`data:${photo.image_mime_type};base64,${photo.image_base64}`}
                  alt={photo.cat_name || "Cat photo"}
                  className="w-full h-full object-cover"
                />
                {/* Selection Checkbox */}
                <div 
                  className="absolute top-2 right-2 z-10"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(photo.photo_id); }}
                >
                  <button className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                    selectedIds.includes(photo.photo_id) 
                      ? "bg-amber-500 border-amber-500 text-white" 
                      : "bg-white/80 border-stone-300 hover:border-amber-500"
                  }`}>
                    {selectedIds.includes(photo.photo_id) && <Check className="w-4 h-4" />}
                  </button>
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <p className="text-white font-medium">View Details</p>
                </div>
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <Badge className={STATUS_COLORS[photo.status]}>
                    {photo.status}
                  </Badge>
                </div>
                {/* Featured Star */}
                {photo.featured && (
                  <div className="absolute top-2 right-2">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400 drop-shadow" />
                  </div>
                )}
                {/* Adopted Badge */}
                {photo.adopted_from_catpals && (
                  <div className="absolute bottom-2 left-2">
                    <Badge className="bg-purple-600 text-white">
                      <Cat className="w-3 h-3 mr-1" />
                      CatPals+ Cat
                    </Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <p className="font-medium text-stone-800 truncate">{photo.cat_name || "Unnamed"}</p>
                <p className="text-sm text-stone-500 truncate">by {photo.submitter_name}</p>
                {photo.status === "pending" && (
                  <div className="flex gap-2 mt-2" onClick={e => e.stopPropagation()}>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(photo.photo_id)}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(photo.photo_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Photo Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Photo Details</DialogTitle>
          </DialogHeader>
          
          {selectedPhoto && (
            <div className="space-y-4">
              {/* Image */}
              <div className="rounded-lg overflow-hidden bg-stone-100">
                <img
                  src={`data:${selectedPhoto.image_mime_type};base64,${selectedPhoto.image_base64}`}
                  alt={selectedPhoto.cat_name || "Cat photo"}
                  className="w-full max-h-[400px] object-contain"
                />
              </div>

              {/* Status & Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[selectedPhoto.status]}>
                    {selectedPhoto.status}
                  </Badge>
                  {selectedPhoto.featured && (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Featured
                    </Badge>
                  )}
                  {selectedPhoto.adopted_from_catpals && (
                    <Badge className="bg-purple-100 text-purple-700">
                      <Cat className="w-3 h-3 mr-1" />
                      CatPals+ Cat
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-stone-500">
                  <Heart className="w-4 h-4" />
                  <span>{selectedPhoto.likes || 0}</span>
                </div>
              </div>

              {/* Cat Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-stone-800 mb-2">
                    {selectedPhoto.cat_name || "Unnamed Cat"}
                  </h3>
                  {selectedPhoto.caption && (
                    <p className="text-stone-600">{selectedPhoto.caption}</p>
                  )}
                </div>
                
                {/* Submitter Info */}
                <div className="bg-stone-50 rounded-lg p-4">
                  <h4 className="font-medium text-stone-700 mb-2">Submitted by</h4>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <User className="w-4 h-4 text-stone-400" />
                      {selectedPhoto.submitter_name}
                    </p>
                    {selectedPhoto.submitter_email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-stone-400" />
                        {selectedPhoto.submitter_email}
                      </p>
                    )}
                    {selectedPhoto.instagram_handle && (
                      <p className="flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-stone-400" />
                        {selectedPhoto.instagram_handle}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Dates */}
              <p className="text-sm text-stone-500">
                Submitted: {formatDate(selectedPhoto.created_at)}
                {selectedPhoto.approved_at && ` • Approved: ${formatDate(selectedPhoto.approved_at)}`}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedPhoto.status === "pending" && (
                  <>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { handleApprove(selectedPhoto.photo_id); setShowModal(false); }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => { handleReject(selectedPhoto.photo_id); setShowModal(false); }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
                {selectedPhoto.status === "approved" && (
                  <Button 
                    variant="outline"
                    onClick={() => toggleFeatured(selectedPhoto.photo_id, selectedPhoto.featured)}
                  >
                    <Star className={`w-4 h-4 mr-2 ${selectedPhoto.featured ? "fill-yellow-400 text-yellow-400" : ""}`} />
                    {selectedPhoto.featured ? "Remove Featured" : "Mark Featured"}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="text-red-600 ml-auto"
                  onClick={() => handleDelete(selectedPhoto.photo_id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHashtagGalleryTab;
